// MessageTemplateModal.tsx
//
// O modal que a aba "Alertas e Notificações" chama em
// `handleOpenModal("messageTemplate", template)` e que nunca existiu: clicar num
// template não abria nada.
//
// ONDE ISTO GRAVA: `acd."ActOrganizationInfo"`, `organizationInfoType` 20 (ver
// `templatesPadrao.ts` para o porquê do 20 — o enum do legado vai só até 8).
// A rota existe: `POST /organizationInfo` e `PATCH /organizationInfo/:uuid`,
// ambas com `Body: any` (organization.routes.ts). Quem monta o corpo é o
// `handleSave` do shell; se o `case "messageTemplate"` ainda não estiver lá, o
// `default` recusa por nome, sem mandar requisição, e a recusa aparece aqui.
//
// AS VARIÁVEIS NÃO SÃO DIGITADAS. São derivadas do texto por regex, ao vivo. O
// dado antigo guardava `content` e `variables` em campos separados: as duas
// listas nascem iguais e divergem na primeira edição do texto, e aí o cliente
// recebe "{ponto_reposicao}" cru na mensagem.
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/_casca";
import * as EstiloProprio from "../../../styles/components/configuration/modals/MessageTemplateModal";
import {
  CanalDeTemplate,
  ORGANIZATION_INFO_TEMPLATE,
  ROTULO_CANAL,
  extrairVariaveis,
} from "../sections/suppliesSection/templatesPadrao";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

const CANAIS: CanalDeTemplate[] = ["whatsapp", "email", "sms"];

/**
 * Valores de exemplo da prévia. Não são dados do cliente e não vêm do banco:
 * são amostras fixas, para o usuário ver a FORMA da mensagem antes de salvar.
 * Variável fora desta lista aparece como «nome» — é o que denuncia o erro de
 * digitação em `{produtoo}` sem precisar mandar a mensagem para descobrir.
 */
const VALORES_DE_EXEMPLO: Record<string, string> = {
  produto: "Farinha de Trigo 5kg",
  loja: "Pizzaria Pompeia",
  quantidade: "12",
  unidade: "sc",
  ponto_reposicao: "20",
  estoque_atual: "8",
  validade: "30/09/2026",
  dias_restantes: "7",
  codigo_ordem: "OC-2026-0142",
  fornecedor: "Massa Fresca Ltda",
  data: "06/08/2026",
  valor_total: "R$ 1.284,50",
  preco_unitario: "R$ 107,04",
  subtotal_linha: "R$ 1.284,48",
};

function renderizarPrevia(conteudo: string): string {
  return (conteudo || "").replace(
    /\{([a-zA-Z0-9_]+)\}/g,
    (_bruto, nome: string) => VALORES_DE_EXEMPLO[nome] ?? `«${nome}»`
  );
}

interface MessageTemplateModalProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
}

const MessageTemplateModal: React.FC<MessageTemplateModalProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-messagetemplatemodal",
  });

  const [form, setForm] = useState<{
    nome: string;
    canal: CanalDeTemplate;
    conteudo: string;
  }>({
    nome: "",
    canal: "whatsapp",
    conteudo: "",
  });
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  // Quem salva com sucesso tem o modal fechado pelo shell; sem esta trava o
  // `finally` escreveria estado num componente já desmontado.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    // Dois formatos de entrada: o objeto já lido pela aba
    // (`lerTemplatesGravados`) e a linha crua do token, com o JSON em
    // `organizationInfoValue`. Ler os dois evita abrir vazio em cima de um
    // template que existe.
    const doValorCru = selectedObject?.organizationInfoValue
      ? parseInfoValue(selectedObject.organizationInfoValue)
      : {};

    const canal = selectedObject?.canal ?? doValorCru?.canal;

    setForm({
      nome: selectedObject?.nome ?? doValorCru?.nome ?? "",
      canal: CANAIS.includes(canal) ? canal : "whatsapp",
      conteudo: selectedObject?.conteudo ?? doValorCru?.conteudo ?? "",
    });
    setAviso(null);
    // Só o `selectedObject`. `parseInfoValue` muda de identidade a cada render
    // de Configuration, e `handleSave` re-renderiza o pai (`updateState({
    // isEditing: false })`) antes de devolver o resultado: com ela na lista,
    // este efeito rodava depois de toda recusa e apagava o texto do template
    // que o usuário acabou de escrever, junto com o aviso que explicava a
    // recusa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObject]);

  const handleLocalChange = (campo: string, valor: any) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  // Derivadas do texto a cada tecla: é a lista que vai junto no payload, para
  // não existir uma segunda lista para divergir.
  const variaveis = useMemo(
    () => extrairVariaveis(form.conteudo),
    [form.conteudo]
  );
  const previa = useMemo(
    () => renderizarPrevia(form.conteudo),
    [form.conteudo]
  );
  const semExemplo = useMemo(
    () => variaveis.filter((nome) => VALORES_DE_EXEMPLO[nome] === undefined),
    [variaveis]
  );

  const ehEdicao = Boolean(selectedObject?.organizationInfoUuid);

  const handleSubmit = async () => {
    if (!form.nome.trim()) {
      setAviso({ tom: "erro", texto: "Dê um nome ao template." });
      return;
    }
    if (!form.conteudo.trim()) {
      setAviso({
        tom: "erro",
        texto: "Escreva a mensagem. Template vazio enviaria mensagem vazia.",
      });
      return;
    }
    // Chave aberta (`{produto` sem fechar) passa reto pela regex e é enviada
    // crua ao cliente. Pegar aqui custa uma contagem; pegar depois custa uma
    // mensagem errada num WhatsApp de cliente.
    const abertas = (form.conteudo.match(/\{/g) || []).length;
    const fechadas = (form.conteudo.match(/\}/g) || []).length;
    if (abertas !== fechadas) {
      setAviso({
        tom: "erro",
        texto:
          `A mensagem tem ${abertas} "{" e ${fechadas} "}". Alguma variável ` +
          "ficou sem fechar e sairia crua na mensagem enviada.",
      });
      return;
    }

    setSalvando(true);
    try {
      const resultado = await handleSave("messageTemplate", {
        organizationInfoUuid: selectedObject?.organizationInfoUuid,
        organizationInfoType: ORGANIZATION_INFO_TEMPLATE,
        nome: form.nome.trim(),
        canal: form.canal,
        conteudo: form.conteudo,
        // Vai junto só como cópia derivada, para quem lê o JSON no banco não
        // precisar rodar a regex. A verdade continua sendo o `conteudo`.
        variaveis,
      });

      if (resultado && !resultado.ok && montado.current) {
        setAviso({ tom: resultado.tom ?? "erro", texto: resultado.texto });
      }
    } finally {
      if (montado.current) setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} medium>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-messagetemplatemodal">
            <AlfredoStyles.MaterialIcons>
              description
            </AlfredoStyles.MaterialIcons>
            {/* `{}` do useModal é truthy — quem diz se é edição é o uuid. Um
                template padrão do sistema (semente, sem uuid) abre como NOVO,
                que é o que ele é: ainda não existe linha no banco. */}
            {ehEdicao ? "Editar Template" : "Novo Template"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            {!ehEdicao && selectedObject?.padraoDoSistema && (
              <AlfredoStyles.AlertMessage $tom="atencao">
                Este é um template PADRÃO DO SISTEMA, ainda não gravado. Salvar
                cria a primeira versão da sua organização; o padrão continua
                existindo no código como semente.
              </AlfredoStyles.AlertMessage>
            )}

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Identificação
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Nome do template *</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="text"
                    value={form.nome}
                    onChange={(e) => handleLocalChange("nome", e.target.value)}
                    placeholder="Ex: Ordem de Compra — cabeçalho"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Canal</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.canal}
                    onChange={(e) => handleLocalChange("canal", e.target.value)}
                  >
                    {CANAIS.map((canal) => (
                      <option key={canal} value={canal}>
                        {ROTULO_CANAL[canal]}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>Mensagem</AlfredoStyles.SectionTitle>

              <AlfredoStyles.LabelInputContainer fullWidth>
                <AlfredoStyles.Label>
                  Conteúdo * — use {"{variavel}"} onde entra o dado
                </AlfredoStyles.Label>
                <AlfredoStyles.TextArea
                  value={form.conteudo}
                  onChange={(e) =>
                    handleLocalChange("conteudo", e.target.value)
                  }
                  placeholder={
                    "Ex: Ordem {codigo_ordem} para {fornecedor}. Total: {valor_total}"
                  }
                  rows={8}
                />
              </AlfredoStyles.LabelInputContainer>

              <AlfredoStyles.Label style={{ marginTop: "var(--sp-12)" }}>
                Variáveis encontradas no texto
              </AlfredoStyles.Label>
              <EstiloProprio.LinhaDeChips>
                {variaveis.length === 0 ? (
                  <AlfredoStyles.InfoBadge>
                    nenhuma — a mensagem sai igual para todo mundo
                  </AlfredoStyles.InfoBadge>
                ) : (
                  variaveis.map((nome) => (
                    <AlfredoStyles.InfoBadge key={nome}>
                      {`{${nome}}`}
                    </AlfredoStyles.InfoBadge>
                  ))
                )}
              </EstiloProprio.LinhaDeChips>

              {semExemplo.length > 0 && (
                <AlfredoStyles.AlertMessage $tom="atencao">
                  Sem valor de exemplo para {semExemplo.join(", ")} — na prévia
                  elas aparecem como «nome». Confira a grafia: quem envia a
                  mensagem só substitui o que souber preencher.
                </AlfredoStyles.AlertMessage>
              )}
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>Prévia</AlfredoStyles.SectionTitle>
              <EstiloProprio.PreviaCaixa>
                {previa || "(a mensagem está vazia)"}
              </EstiloProprio.PreviaCaixa>
              <EstiloProprio.PreviaLegenda>
                <AlfredoStyles.MaterialIcons
                  style={{ fontSize: "var(--fs-base)" }}
                >
                  info
                </AlfredoStyles.MaterialIcons>
                Valores de exemplo, não dados reais. Serve para conferir a forma
                da mensagem — em {ROTULO_CANAL[form.canal]}.
              </EstiloProprio.PreviaLegenda>
            </AlfredoStyles.FormSection>

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando
                ? "Salvando…"
                : ehEdicao
                ? "Atualizar Template"
                : "Criar Template"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default MessageTemplateModal;
